import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Save, Plus, Trash2 } from 'lucide-react';
import ImageUpload from '@/components/ImageUpload';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getToken = () => localStorage.getItem('admin_token');
const authHeaders = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

export default function AdminSiteContent() {
  const [content, setContent] = useState({
    carousel_slides: [], about_title: '', about_content: '', about_image: '',
    address: '', phone: '', email: '', opening_hours: {}, map_embed: '',
    whatsapp_number: '', instagram_link: '',
    seo_title: '', seo_description: '', seo_keywords: '',
    google_analytics_id: '', google_search_console: '', facebook_pixel_id: '',
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    axios.get(`${API}/site-content`).then(r => {
      if (r.data && Object.keys(r.data).length > 0) setContent(r.data);
    }).catch(() => {});
  }, []);

  const handleSave = async () => {
    setSaving(true); setSaved(false);
    try {
      const { type, created_at, updated_at, ...payload } = content;
      await axios.put(`${API}/admin/site-content`, payload, authHeaders());
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch { alert('Error saving content'); }
    finally { setSaving(false); }
  };

  const updateField = (key, val) => setContent(prev => ({ ...prev, [key]: val }));

  const updateSlide = (i, key, val) => {
    const slides = [...content.carousel_slides];
    slides[i] = { ...slides[i], [key]: val };
    updateField('carousel_slides', slides);
  };

  const addSlide = () => updateField('carousel_slides', [...content.carousel_slides, { image: '', title: '', subtitle: '', cta_text: '', cta_link: '' }]);
  const removeSlide = (i) => updateField('carousel_slides', content.carousel_slides.filter((_, idx) => idx !== i));

  const updateHour = (day, val) => updateField('opening_hours', { ...content.opening_hours, [day]: val });

  return (
    <div data-testid="admin-site-content-page">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-heading text-2xl font-medium">Site Content</h1>
        <Button className="rounded-xl" onClick={handleSave} disabled={saving} data-testid="save-content-btn">
          <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : saved ? 'Saved!' : 'Save Changes'}
        </Button>
      </div>

      <div className="space-y-8">
        {/* Carousel */}
        <Card className="rounded-2xl border-border">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-lg font-medium">Carousel Slides</h2>
              <Button variant="outline" size="sm" className="rounded-lg" onClick={addSlide} data-testid="add-slide-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Add Slide</Button>
            </div>
            {content.carousel_slides.map((slide, i) => (
              <div key={i} className="p-4 bg-muted/50 rounded-xl mb-3 space-y-3" data-testid={`slide-${i}`}>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Slide {i + 1}</span>
                  <Button variant="ghost" size="sm" className="text-destructive" onClick={() => removeSlide(i)} data-testid={`remove-slide-${i}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="md:col-span-2"><ImageUpload value={slide.image} onChange={v => updateSlide(i, 'image', v)} label="Slide Image" /></div>
                  <div><Label className="text-xs">Title</Label><Input value={slide.title} onChange={e => updateSlide(i, 'title', e.target.value)} className="mt-1 rounded-lg text-sm" /></div>
                  <div><Label className="text-xs">Subtitle</Label><Input value={slide.subtitle} onChange={e => updateSlide(i, 'subtitle', e.target.value)} className="mt-1 rounded-lg text-sm" /></div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label className="text-xs">CTA Text</Label><Input value={slide.cta_text} onChange={e => updateSlide(i, 'cta_text', e.target.value)} className="mt-1 rounded-lg text-sm" /></div>
                    <div><Label className="text-xs">CTA Link</Label><Input value={slide.cta_link} onChange={e => updateSlide(i, 'cta_link', e.target.value)} className="mt-1 rounded-lg text-sm" /></div>
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* About */}
        <Card className="rounded-2xl border-border">
          <CardContent className="p-6 space-y-4">
            <h2 className="font-heading text-lg font-medium">About Section</h2>
            <div><Label className="text-sm">Title</Label><Input value={content.about_title} onChange={e => updateField('about_title', e.target.value)} className="mt-1 rounded-xl" data-testid="about-title-input" /></div>
            <div><Label className="text-sm">Content</Label><Textarea value={content.about_content} onChange={e => updateField('about_content', e.target.value)} rows={5} className="mt-1 rounded-xl" data-testid="about-content-input" /></div>
            <ImageUpload value={content.about_image} onChange={v => updateField('about_image', v)} label="About Section Image" />
          </CardContent>
        </Card>

        {/* Contact Info */}
        <Card className="rounded-2xl border-border">
          <CardContent className="p-6 space-y-4">
            <h2 className="font-heading text-lg font-medium">Contact Information</h2>
            <div><Label className="text-sm">Address</Label><Textarea value={content.address} onChange={e => updateField('address', e.target.value)} rows={2} className="mt-1 rounded-xl" data-testid="address-input" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="text-sm">Phone</Label><Input value={content.phone} onChange={e => updateField('phone', e.target.value)} className="mt-1 rounded-xl" data-testid="phone-input" /></div>
              <div><Label className="text-sm">Email</Label><Input value={content.email} onChange={e => updateField('email', e.target.value)} className="mt-1 rounded-xl" data-testid="email-input" /></div>
            </div>
          </CardContent>
        </Card>

        {/* Opening Hours */}
        <Card className="rounded-2xl border-border">
          <CardContent className="p-6">
            <h2 className="font-heading text-lg font-medium mb-4">Opening Hours</h2>
            <div className="space-y-3">
              {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => (
                <div key={day} className="flex items-center gap-4">
                  <span className="text-sm font-medium w-24">{day}</span>
                  <Input value={content.opening_hours?.[day] || ''} onChange={e => updateHour(day, e.target.value)} className="rounded-lg text-sm" data-testid={`hours-${day.toLowerCase()}`} placeholder="e.g. 8:00 AM - 6:00 PM" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Social Links */}
        <Card className="rounded-2xl border-border">
          <CardContent className="p-6 space-y-4">
            <h2 className="font-heading text-lg font-medium">Social Links & Messaging</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><Label className="text-sm">WhatsApp Number (without +)</Label><Input value={content.whatsapp_number} onChange={e => updateField('whatsapp_number', e.target.value)} className="mt-1 rounded-xl" data-testid="whatsapp-input" placeholder="15485322279" /></div>
              <div><Label className="text-sm">Instagram Link</Label><Input value={content.instagram_link} onChange={e => updateField('instagram_link', e.target.value)} className="mt-1 rounded-xl" data-testid="instagram-input" placeholder="https://instagram.com/yourclinic" /></div>
            </div>
          </CardContent>
        </Card>

        {/* SEO */}
        <Card className="rounded-2xl border-border">
          <CardContent className="p-6 space-y-4">
            <h2 className="font-heading text-lg font-medium">SEO Settings</h2>
            <div><Label className="text-sm">Page Title (SEO)</Label><Input value={content.seo_title} onChange={e => updateField('seo_title', e.target.value)} className="mt-1 rounded-xl" data-testid="seo-title-input" placeholder="Dr. Ritika Women's Clinic | Safe Healthcare" /></div>
            <div><Label className="text-sm">Meta Description</Label><Textarea value={content.seo_description} onChange={e => updateField('seo_description', e.target.value)} rows={2} className="mt-1 rounded-xl" data-testid="seo-desc-input" placeholder="Trusted women's healthcare clinic providing safe, confidential services..." /></div>
            <div><Label className="text-sm">Keywords (comma separated)</Label><Input value={content.seo_keywords} onChange={e => updateField('seo_keywords', e.target.value)} className="mt-1 rounded-xl" data-testid="seo-keywords-input" placeholder="women clinic, healthcare, gynecology, abortion, mifepristone" /></div>
          </CardContent>
        </Card>

        {/* Tracking & Analytics */}
        <Card className="rounded-2xl border-border">
          <CardContent className="p-6 space-y-4">
            <h2 className="font-heading text-lg font-medium">Tracking & Analytics</h2>
            <div><Label className="text-sm">Google Analytics ID</Label><Input value={content.google_analytics_id} onChange={e => updateField('google_analytics_id', e.target.value)} className="mt-1 rounded-xl" data-testid="ga-input" placeholder="G-XXXXXXXXXX" /></div>
            <div><Label className="text-sm">Google Search Console Verification</Label><Input value={content.google_search_console} onChange={e => updateField('google_search_console', e.target.value)} className="mt-1 rounded-xl" data-testid="gsc-input" placeholder="google-site-verification code" /></div>
            <div><Label className="text-sm">Facebook Pixel ID</Label><Input value={content.facebook_pixel_id} onChange={e => updateField('facebook_pixel_id', e.target.value)} className="mt-1 rounded-xl" data-testid="fbpixel-input" placeholder="Your Facebook Pixel ID" /></div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
